import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventsAdmin = () => {
  const { getAuthHeaders } = useAuth();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    type: 'Home Buyers',
    date: '',
    time: '',
    location: '',
    description: '',
    topics: [],
    registration_url: '',
    capacity: '',
    instructor: '',
    ce_credits: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/events`, { headers });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const headers = getAuthHeaders();
      const eventData = {
        ...formData,
        topics: formData.topics.filter(topic => topic.trim() !== '')
      };
      const response = await axios.post(`${API}/admin/events`, eventData, { headers });
      setEvents([...events, response.data]);
      setIsCreating(false);
      resetForm();
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Error creating event');
    }
  };

  const handleUpdate = async (id) => {
    try {
      const headers = getAuthHeaders();
      const eventData = {
        ...formData,
        topics: formData.topics.filter(topic => topic.trim() !== '')
      };
      const response = await axios.put(`${API}/admin/events/${id}`, eventData, { headers });
      setEvents(events.map(event => 
        event.id === id ? response.data : event
      ));
      setEditingEvent(null);
      resetForm();
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Error updating event');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/events/${id}`, { headers });
      setEvents(events.filter(event => event.id !== id));
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Error deleting event');
    }
  };

  const startEdit = (event) => {
    setEditingEvent(event.id);
    setFormData({
      title: event.title,
      type: event.type,
      date: event.date,
      time: event.time,
      location: event.location,
      description: event.description,
      topics: event.topics || [],
      registration_url: event.registration_url,
      capacity: event.capacity,
      instructor: event.instructor,
      ce_credits: event.ce_credits || ''
    });
  };

  const cancelEdit = () => {
    setEditingEvent(null);
    setIsCreating(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'Home Buyers',
      date: '',
      time: '',
      location: '',
      description: '',
      topics: [],
      registration_url: '',
      capacity: '',
      instructor: '',
      ce_credits: ''
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTopicsChange = (index, value) => {
    const newTopics = [...formData.topics];
    newTopics[index] = value;
    setFormData({
      ...formData,
      topics: newTopics
    });
  };

  const addTopic = () => {
    setFormData({
      ...formData,
      topics: [...formData.topics, '']
    });
  };

  const removeTopic = (index) => {
    const newTopics = formData.topics.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      topics: newTopics
    });
  };

  if (isLoading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2c4a] mx-auto mb-4"></div>
        <p className="text-gray-600">Loading events...</p>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" asChild>
                <Link to="/admin" className="flex items-center space-x-2">
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Dashboard</span>
                </Link>
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Events Management</h1>
            </div>
            <Button
              className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
              onClick={() => setIsCreating(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Event
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create Form */}
        {isCreating && (
          <Card className="mb-8 border-none bg-white">
            <CardHeader>
              <CardTitle>Add New Event</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Event Title</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g. First-Time Homebuyer Workshop"
                  />
                </div>
                <div>
                  <Label htmlFor="type">Event Type</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Home Buyers">Home Buyers</SelectItem>
                      <SelectItem value="Real Estate Agents">Real Estate Agents</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    placeholder="March 15, 2025"
                  />
                </div>
                <div>
                  <Label htmlFor="time">Time</Label>
                  <Input
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    placeholder="10:00 AM - 12:00 PM"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Virtual Webinar or Physical Address"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Event description..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="registration_url">Registration URL</Label>
                  <Input
                    id="registration_url"
                    name="registration_url"
                    value={formData.registration_url}
                    onChange={handleInputChange}
                    placeholder="https://your-ghl-funnel-url.com"
                  />
                </div>
                <div>
                  <Label htmlFor="capacity">Capacity</Label>
                  <Input
                    id="capacity"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    placeholder="50 attendees"
                  />
                </div>
                <div>
                  <Label htmlFor="instructor">Instructor</Label>
                  <Input
                    id="instructor"
                    name="instructor"
                    value={formData.instructor}
                    onChange={handleInputChange}
                    placeholder="John Smith, Senior Loan Advisor"
                  />
                </div>
                <div>
                  <Label htmlFor="ce_credits">CE Credits (Optional)</Label>
                  <Input
                    id="ce_credits"
                    name="ce_credits"
                    value={formData.ce_credits}
                    onChange={handleInputChange}
                    placeholder="2 CE Credits Available"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Topics Covered</Label>
                  {formData.topics.map((topic, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <Input
                        value={topic}
                        onChange={(e) => handleTopicsChange(index, e.target.value)}
                        placeholder="Enter topic..."
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeTopic(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTopic}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Topic
                  </Button>
                </div>
              </div>
              <div className="flex space-x-4 mt-6">
                <Button
                  className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                  onClick={handleCreate}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Event
                </Button>
                <Button variant="outline" onClick={cancelEdit}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Events List */}
        <div className="grid gap-6">
          {events.map((event) => (
            <Card key={event.id} className="border-none bg-white">
              <CardContent className="p-6">
                {editingEvent === event.id ? (
                  // Edit Form (similar to create form but with update logic)
                  <div>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <Label htmlFor={`title-${event.id}`}>Event Title</Label>
                        <Input
                          id={`title-${event.id}`}
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`type-${event.id}`}>Event Type</Label>
                        <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Home Buyers">Home Buyers</SelectItem>
                            <SelectItem value="Real Estate Agents">Real Estate Agents</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor={`date-${event.id}`}>Date</Label>
                        <Input
                          id={`date-${event.id}`}
                          name="date"
                          value={formData.date}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`time-${event.id}`}>Time</Label>
                        <Input
                          id={`time-${event.id}`}
                          name="time"
                          value={formData.time}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor={`location-${event.id}`}>Location</Label>
                        <Input
                          id={`location-${event.id}`}
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor={`description-${event.id}`}>Description</Label>
                        <Textarea
                          id={`description-${event.id}`}
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`registration_url-${event.id}`}>Registration URL</Label>
                        <Input
                          id={`registration_url-${event.id}`}
                          name="registration_url"
                          value={formData.registration_url}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`capacity-${event.id}`}>Capacity</Label>
                        <Input
                          id={`capacity-${event.id}`}
                          name="capacity"
                          value={formData.capacity}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`instructor-${event.id}`}>Instructor</Label>
                        <Input
                          id={`instructor-${event.id}`}
                          name="instructor"
                          value={formData.instructor}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`ce_credits-${event.id}`}>CE Credits</Label>
                        <Input
                          id={`ce_credits-${event.id}`}
                          name="ce_credits"
                          value={formData.ce_credits}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="flex space-x-4">
                      <Button
                        className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                        onClick={() => handleUpdate(event.id)}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={cancelEdit}>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Display Mode
                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-16 h-16 rounded-lg bg-[#0b2c4a]/10 flex items-center justify-center flex-shrink-0">
                          <Calendar className="h-8 w-8 text-[#0b2c4a]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900">{event.title}</h3>
                          <p className="text-[#0b2c4a] font-medium">{event.type}</p>
                          <p className="text-sm text-gray-600">{event.date} • {event.time}</p>
                          <p className="text-sm text-gray-600">{event.location}</p>
                          {event.ce_credits && (
                            <p className="text-sm text-green-600 font-medium">{event.ce_credits}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEdit(event)}
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(event.id)}
                          className="text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-gray-700 text-sm leading-relaxed">{event.description}</p>
                    </div>
                    
                    {event.topics && event.topics.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Topics Covered:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {event.topics.map((topic, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <span className="text-[#0b2c4a] mt-1">•</span>
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <p><strong>Capacity:</strong> {event.capacity}</p>
                        <p><strong>Instructor:</strong> {event.instructor}</p>
                      </div>
                      <div>
                        <p><strong>Registration:</strong> <a href={event.registration_url} target="_blank" rel="noopener noreferrer" className="text-[#0b2c4a] hover:underline">Registration Link</a></p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {events.length === 0 && (
          <Card className="border-none bg-white">
            <CardContent className="p-12 text-center">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Events</h3>
              <p className="text-gray-600 mb-4">Get started by creating your first event.</p>
              <Button
                className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                onClick={() => setIsCreating(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Event
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EventsAdmin;